/*
 * <collection-stories>: horizontal story row used by the GD Collection Stories section
 * and the Collection Stories block in GD Image with Text / GD Slider with Text.
 * Handles edge fades, mouse drag-to-scroll, and (for blocks) bleeding to the container edges.
 */
if (!customElements.get('collection-stories')) {
  customElements.define(
    'collection-stories',
    class CollectionStories extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-stories-list]');
        if (!this.list) return;

        this.update = this.update.bind(this);
        this.updateBleed = this.updateBleed.bind(this);

        // Blocks extend into the padding of their content container so the row reaches its edges.
        // Measured instead of hard-coded because sections and custom CSS set different padding.
        this.bleedHost = this.closest('[data-stories-bleed]');
        if (this.bleedHost) {
          this.bleedContainer = this.bleedHost.parentElement;
          this.updateBleed();
          window.addEventListener('resize', this.updateBleed);
          if ('ResizeObserver' in window) {
            this.bleedObserver = new ResizeObserver(this.updateBleed);
            this.bleedObserver.observe(this.bleedContainer);
          }
        }
        this.pointerDown = false;
        this.dragged = false;

        this.list.addEventListener('scroll', this.update, { passive: true });
        window.addEventListener('resize', this.update);

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.update);
          this.resizeObserver.observe(this.list);
          Array.from(this.list.children).forEach((child) => this.resizeObserver.observe(child));
        }

        this.list.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', this.update, { once: true });
        });

        this.initDrag();
        this.update();
      }

      disconnectedCallback() {
        if (this.list) this.list.removeEventListener('scroll', this.update);
        window.removeEventListener('resize', this.update);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.bleedHost) window.removeEventListener('resize', this.updateBleed);
        if (this.bleedObserver) this.bleedObserver.disconnect();
      }

      updateBleed() {
        const style = getComputedStyle(this.bleedContainer);
        this.bleedHost.style.setProperty('--stories-bleed-left', style.paddingLeft);
        this.bleedHost.style.setProperty('--stories-bleed-right', style.paddingRight);
      }

      update() {
        const list = this.list;
        if (!list) return;

        const maxScroll = list.scrollWidth - list.clientWidth;
        const scrollable = maxScroll > 1;

        list.classList.toggle('is-scrollable', scrollable);
        list.classList.toggle('is-faded-start', scrollable && list.scrollLeft > 1);
        list.classList.toggle('is-faded-end', scrollable && list.scrollLeft < maxScroll - 1);
      }

      initDrag() {
        const list = this.list;
        // Movement (px) before a press counts as a drag. Below this it stays a normal click.
        const DRAG_THRESHOLD = 8;
        let startX = 0;
        let startScroll = 0;
        let pointerId = null;

        const endDrag = () => {
          if (!this.pointerDown) return;
          this.pointerDown = false;
          list.classList.remove('is-dragging');
          if (pointerId !== null && list.hasPointerCapture(pointerId)) list.releasePointerCapture(pointerId);
          pointerId = null;
        };

        list.addEventListener('pointerdown', (event) => {
          this.dragged = false;
          if (event.pointerType === 'touch' || event.button !== 0) return;
          if (list.scrollWidth - list.clientWidth <= 1) return;

          // No pointer capture yet: capturing here retargets the click to the list,
          // so the link under the cursor never receives it.
          this.pointerDown = true;
          pointerId = event.pointerId;
          startX = event.clientX;
          startScroll = list.scrollLeft;
        });

        list.addEventListener('pointermove', (event) => {
          if (!this.pointerDown) return;

          const delta = event.clientX - startX;
          if (!this.dragged) {
            if (Math.abs(delta) < DRAG_THRESHOLD) return;
            this.dragged = true;
            list.classList.add('is-dragging');
            list.setPointerCapture(pointerId);
          }
          list.scrollLeft = startScroll - delta;
          event.preventDefault();
        });

        list.addEventListener('pointerup', endDrag);
        list.addEventListener('pointercancel', endDrag);
        list.addEventListener('lostpointercapture', endDrag);
        list.addEventListener('dragstart', (event) => {
          if (this.pointerDown) event.preventDefault();
        });

        // Swallow only the click that ends a real drag.
        list.addEventListener(
          'click',
          (event) => {
            if (!this.dragged) return;
            event.preventDefault();
            event.stopPropagation();
            this.dragged = false;
          },
          true
        );
      }
    }
  );
}
